/**
 * ActionButtons Component
 *
 * A reusable component that displays the main action buttons (Duplicate, Export, Reset, Request Quote, Save)
 * for the Room Designer application. This component is separated from DesignContainer to prevent
 * overlap issues with the Equipment List view.
 */

import React, { useState } from 'react';
import { Box, Stack, styled } from '@mui/material';
import { useTheme } from 'theme/ThemeContext';
import { useSelector, useDispatch } from 'react-redux';
import {
  Save as SaveIcon,
  ContentCopy as DuplicateIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import RdButton from '../atoms/RdButton';
import ThemeToggle from '../atoms/ThemeToggle';
import ConfirmDialog from './ConfirmDialog';
import ExportDialog from './ExportDialog';
import DesignNameDialog from './DesignNameDialog';
import { useDirectSave } from '../services/DirectSave';
import { useToast } from '../../contexts/ToastContext';
import { exportDesignsAsPDF, exportAsWorkplaceDesigns, copySingleDesign } from '../../services/designsService';
import { getCurrentView } from 'reduxModules/ducks/ui';
import ReducersIndex from 'reduxModules/ducks';

interface ActionButtonsProps {
  position?: 'absolute' | 'relative';
  bottom?: string | number;
  right?: string | number;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  position = 'absolute',
  bottom = '0',
  right = '0'
}) => {
  const { mode } = useTheme();
  const dispatch = useDispatch();

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Get data from Redux
  const savekey = useSelector((state: any) => state.ui.savekey);
  const needsResave = useSelector((state: any) => state.ui.needsResave);
  const locationId = useSelector((state: any) => state.container.project?.locationId || '');
  const currentView = useSelector((state: any) => (getCurrentView as any)(state)) as string;
  const currentDesignName = useSelector((state: any) => state.container.project?.name || 'Untitled Design');

  const { showSuccess, showError } = useToast();

  // Initialize save functionality
  const { saveDesign } = useDirectSave({
    organizationId: sessionStorage.getItem('selectedOrgId') || '',
    locationId: locationId,
    objectType: 'Conference Room'
  });

  const handleSave = async () => {
    await saveDesign();
  };

  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // Get current container state from Redux
  const containerState = useSelector((state: any) => state.container);
  const units = useSelector((state: any) => state.ui.units);

  const handleExportPDF = async () => {
    const organizationId = sessionStorage.getItem('selectedOrgId') || '';

    if (!savekey) {
      showError('Please save your design before exporting.');
      return;
    }

    if (needsResave) {
      showError('You have unsaved changes. Please save your design before exporting.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      // Use savekey if available, otherwise generate a temporary one
      const designId = savekey || `temp-${Date.now()}`;
      
      // Pass current state to the export function
      const blob = await exportDesignsAsPDF([designId], organizationId, { ...containerState, units });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `design-export-${savekey}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('PDF exported successfully!');

    } catch (error) {
      console.error('Error exporting design as PDF:', error);
      showError('Failed to export PDF. Please try again.');
    }
  };

  const handleExportWorkplace = async () => {
    const organizationId = sessionStorage.getItem('selectedOrgId') || '';

    if (!savekey) {
      showError('Please save your design before exporting.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      await exportAsWorkplaceDesigns([savekey], () => {
        showSuccess('Design exported successfully!');
      });

    } catch (error) {
      console.error('Error exporting workplace design:', error);
      showError('Failed to export design. Please try again.');
    }
  };


  const handleRequestQuote = () => {
    dispatch(ReducersIndex.uiReducers.openModal('requestQuote'));
  };

  const handleDuplicate = () => {
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = async (data: { name: string; type?: string; workplaceLocation?: string }) => {
    setDuplicateDialogOpen(false);

    const organizationId = sessionStorage.getItem('selectedOrgId') || '';

    if (!savekey) {
      showError('Please save your design before duplicating.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      const duplicatedDesign = await copySingleDesign({
        designId: savekey,
        newName: data.name,
        targetOrganizationId: organizationId,
        locationId: data.workplaceLocation || locationId
      });
      showSuccess(`Design "${data.name}" created successfully!`);
      
      // Optional: navigate to the new design
      if (duplicatedDesign.newId) {
        window.location.href = `/conference_room_designer?savekey=${duplicatedDesign.newId}`;
      }
    } catch (error) {
      console.error('Error duplicating design:', error);
      showError('Failed to duplicate design. Please try again.');
    }
  };

  const handleReset = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    dispatch(ReducersIndex.container.resetArea(true) as any);
    showSuccess('Design reset successfully!');
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
  };

  // Hide Duplicate and Reset buttons when in Equipment List view
  const isEquipmentList = currentView === 'equipmentlist';

  return (
    <>
      <ActionButtonsContainer position={position} bottom={bottom} right={right}>
        {/* Theme toggle on the left */}
        <Box sx={{ marginRight: 'auto', display: 'flex', alignItems: 'center' }}>
          <ThemeToggle />
        </Box>

        {/* Action buttons on the right */}
        <Stack direction="row" gap="15px">
          {!isEquipmentList && (
            <RdButton
              id="duplicateButton"
              onClick={handleDuplicate}
              startIcon={<DuplicateIcon />}
            >
              Duplicate
            </RdButton>
          )}
          <RdButton
            id="exportButton"
            onClick={handleExport}
            sx={{'& img': {
              filter: (theme) => theme.palette.mode === 'dark'
                ? 'brightness(0) invert(1)'
                : 'none',
            }}}
            startIcon={
              <Box
                component="img"
                src="/images/Export.svg"
                alt="Export"
                sx={{ width: 20, height: 20 }}
              />
            }
          >
            Export
          </RdButton>
          {!isEquipmentList && (
            <RdButton
              id="resetButton"
              onClick={handleReset}
              startIcon={<ResetIcon />}
            >
              Reset
            </RdButton>
          )}
          <RdButton
            id="designContainerQuoteButton"
            onClick={handleRequestQuote}
            startIcon={
              <Box
                component="img"
                src={mode === 'light' ? '/images/quote-light.svg' : '/images/quote-dark.svg'}
                alt="Request quote"
                sx={{ width: 20, height: 20 }}
              />
            }
          >
            Request quote
          </RdButton>
          <RdButton
            id="saveButton"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            buttonType="primary"
          >
            Save
          </RdButton>
        </Stack>
      </ActionButtonsContainer>

      {/* Dialogs */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExportWorkplace={handleExportWorkplace}
        onExportPDF={handleExportPDF}
      />
      
      <DesignNameDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={handleDuplicateConfirm}
        mode="duplicate"
        initialName={`${currentDesignName} - Copy`}
        designType="Conference Room"
        showWorkplaceLocation={!!locationId}
        organizationId={sessionStorage.getItem('selectedOrgId') || ''}
        initialLocationId={locationId}
      />

      <ConfirmDialog
        open={resetDialogOpen}
        title="Reset design"
        message="Are you sure you want to reset this design? This is a permanent action and can not be undone."
        type="warning"
        onConfirm={handleResetConfirm}
        onCancel={handleResetCancel}
        confirmText="Reset"
        cancelText="Cancel"
      />
    </>
  );
};

// Styled component for positioning the action buttons
const ActionButtonsContainer = styled(Box)<ActionButtonsProps>(({ theme, position, bottom, right }) => ({
  position: position || 'absolute',
  bottom: bottom || '0',
  right: right || '0',
  left: 0,
  zIndex: 1001,
  background: theme.palette.background.default,
  pointerEvents: 'auto',
  padding: '10px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: theme.palette.divider,
    opacity: 0.15,
  },
}));

export default ActionButtons;